
import {
  computed, defineComponent, onMounted, ref, Ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Inplace from 'primevue/inplace';
import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import BaseInputText from '@/components/fields/BaseInputText.vue';
import { useStore } from '@/store';
import {
  insertEmbedTemplateInSelectedElement, resetIframe, setTemplateConfigurationKeyInState, TemplateEditorState, updateSectionsInState,
  updateTemplatePreviewDisplay,
} from '@/composables/template-editor/TemplateEditor';
import { checkIfSavePointNeeded, createHistory } from '@/composables/template-editor/History';
import { HistoryType } from '@/types';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';

export default defineComponent({
  name: 'EmbedPosition',

  components: {
    Inplace,
    BaseInputText,
    BaseDropdown,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const currentSelector: Ref<string> = ref(TemplateEditorState.template?.configuration.embedSelector);
    const currentPosition: Ref<string> = ref(TemplateEditorState.template?.configuration.embedPosition);

    const previewSelector = computed(() => TemplateEditorState.template?.configuration.embedSelector);
    const previewPosition = computed(() => TemplateEditorState.template?.configuration.embedPosition);
    const updateHistory = ref(true);

    const availablePositions = ref([
      { label: t('templateBuilder.fields.embedPosition.availablePositions.before'), value: 'before' },
      { label: t('templateBuilder.fields.embedPosition.availablePositions.prepend'), value: 'prepend' },
      { label: t('templateBuilder.fields.embedPosition.availablePositions.append'), value: 'append' },
      { label: t('templateBuilder.fields.embedPosition.availablePositions.after'), value: 'after' },
      { label: t('templateBuilder.fields.embedPosition.availablePositions.replace'), value: 'replace' },
    ]);

    const handleInPlaceClose = () => {
      checkIfSavePointNeeded().then(async () => {
        setTemplateConfigurationKeyInState('embedSelector', currentSelector.value);
        insertEmbedTemplateInSelectedElement();

        const template = getTemplateIframeDocument();
        const spmBody = template.querySelector('#spm_body');
        if (spmBody) {
          spmBody.setAttribute('data-target-element', currentSelector.value);
        }

        resetIframe();

        // Update state
        updateSectionsInState(false);

        createHistory(HistoryType.EMBED_CHANGE_POSITION);

        updateTemplatePreviewDisplay();
      });
    };

    const handlePositionChange = (addHistory = true) => {
      if (addHistory) {
        checkIfSavePointNeeded().then(async () => {
          setTemplateConfigurationKeyInState('embedPosition', currentPosition.value);
          insertEmbedTemplateInSelectedElement();

          const template = getTemplateIframeDocument();
          const spmBody = template.querySelector('#spm_body');
          if (spmBody) {
            spmBody.setAttribute('data-target-element-position', currentPosition.value);
          }

          resetIframe();

          // Update state
          updateSectionsInState(false);

          createHistory(HistoryType.EMBED_CHANGE_POSITION);

          updateTemplatePreviewDisplay();
        });
      } else {
        updateHistory.value = true;
      }
    };

    watch(currentSelector, (newValue) => {
      if (newValue === 'body') {
        currentPosition.value = 'prepend';
        availablePositions.value = [
          { label: t('templateBuilder.fields.embedPosition.availablePositions.prepend'), value: 'prepend' },
          { label: t('templateBuilder.fields.embedPosition.availablePositions.append'), value: 'append' },
        ];
        handlePositionChange(updateHistory.value);
      } else {
        availablePositions.value = [
          { label: t('templateBuilder.fields.embedPosition.availablePositions.before'), value: 'before' },
          { label: t('templateBuilder.fields.embedPosition.availablePositions.prepend'), value: 'prepend' },
          { label: t('templateBuilder.fields.embedPosition.availablePositions.append'), value: 'append' },
          { label: t('templateBuilder.fields.embedPosition.availablePositions.after'), value: 'after' },
          { label: t('templateBuilder.fields.embedPosition.availablePositions.replace'), value: 'replace' },
        ];
      }
    });

    watch(previewSelector, () => {
      if (previewSelector.value !== currentSelector.value) {
        updateHistory.value = false;
        currentPosition.value = previewPosition.value;
        currentSelector.value = previewSelector.value;
      }
    });

    onMounted(() => {
      if (currentSelector.value === 'body') {
        availablePositions.value = [
          { label: t('templateBuilder.fields.embedPosition.availablePositions.prepend'), value: 'prepend' },
          { label: t('templateBuilder.fields.embedPosition.availablePositions.append'), value: 'append' },
        ];
      }
    });

    return {
      t,
      handlePositionChange,
      handleInPlaceClose,
      currentSelector,
      currentPosition,
      availablePositions,
    };
  },
});
